import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  MenuItem,
  Divider
} from '@mui/material';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import api from '../../api/axios';
import toast from 'react-hot-toast';

const OtherAllowanceForm = () => {
  const [branches, setBranches] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [form, setForm] = useState({
    branchId: '',
    employeeId: '',
    type: '',
    amount: '',
    remarks: '',
    month: ''
  });

  useEffect(() => {
    api.get('/branches')
      .then(res => setBranches(res.data))
      .catch(() => toast.error('Failed to load branches'));
  }, []);

  useEffect(() => {
    if (!form.branchId) {
      setEmployees([]);
      return;
    }
    setLoadingEmployees(true);
    api.get(`/employees/branch/${form.branchId}`)
      .then(res => setEmployees(res.data))
      .catch(() => toast.error('Failed to load employees for this branch'))
      .finally(() => setLoadingEmployees(false));
  }, [form.branchId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: value,
      ...(name === 'branchId' ? { employeeId: '' } : {})
    }));
  };

  const handleSubmit = async () => {
    const { employeeId, type, amount, remarks, month, branchId } = form;

    if (!branchId) {
      toast.error('Please select a branch first');
      return;
    }
    if (!employeeId || !type || !amount || !month) {
      toast.error('Please fill all required fields');
      return;
    }

    try {
      await api.post('/other-allowances/create', {
        employeeId: Number(employeeId),
        type,
        amount: Number(amount),
        remarks,
        month
      });

      toast.success('Allowance added successfully');

      setForm({
        branchId,
        employeeId: '',
        type: '',
        amount: '',
        remarks: '',
        month: ''
      });
    } catch (err) {
      toast.error('Failed to create allowance');
    }
  };

  return (
    <Box sx={{ p: 4 }}>
      <Typography variant="h4" fontWeight={800} mb={1}>
        Other Allowances
      </Typography>
      <Typography color="text.secondary" mb={3}>
        Travel, food, bonus, adjustment entries
      </Typography>

      <Paper sx={{ p: 4, maxWidth: 650, borderRadius: 3 }}>
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <RequestQuoteIcon color="warning" />
          <Typography fontWeight={700}>New Allowance</Typography>
        </Box>

        <Divider sx={{ mb: 3 }} />

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              select
              fullWidth
              label="Branch"
              name="branchId"
              value={form.branchId}
              onChange={handleChange}
            >
              {branches.map(b => (
                <MenuItem key={b.id} value={b.id}>
                  {b.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12}>
            <TextField
              select
              fullWidth
              label="Employee"
              name="employeeId"
              value={form.employeeId}
              onChange={handleChange}
              disabled={!form.branchId || loadingEmployees}
              helperText={!form.branchId ? 'Select a branch first' : ''}
            >
              {employees.map(e => (
                <MenuItem key={e.id} value={e.id}>
                  {e.name} ({e.employeeCode})
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Allowance Type"
              name="type"
              placeholder="Travel / Food / Bonus"
              value={form.type}
              onChange={handleChange}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              type="number"
              label="Amount (₹)"
              name="amount"
              value={form.amount}
              onChange={handleChange}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              type="month"
              label="Allowance Month"
              name="month"
              InputLabelProps={{ shrink: true }}
              value={form.month}
              onChange={handleChange}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Remarks"
              name="remarks"
              value={form.remarks}
              onChange={handleChange}
            />
          </Grid>

          <Grid item xs={12}>
            <Button
              fullWidth
              size="large"
              variant="contained"
              color="warning"
              onClick={handleSubmit}
            >
              Save Allowance
            </Button>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default OtherAllowanceForm;